import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Switch,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  deleteRecipientCascade,
  searchRecipients,
  setRecipientActive,
  upsertRecipient,
} from '../../../api/mailingApi';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getUser } from '../../../redux/usersRedux';

const RecipientsPage = () => {
  const user = useSelector((state) => getUser(state));
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    msg: '',
  });

  const showError = (e, fallback) => {
    const msg =
      e?.response?.data?.message || e?.message || fallback || 'Wystąpił błąd';
    setSnack({ open: true, severity: 'error', msg: String(msg) });
  };

  const showOk = (msg) => {
    setSnack({ open: true, severity: 'success', msg });
  };

  async function load(query) {
    setLoading(true);
    try {
      const data = await searchRecipients(query);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      showError(e, 'Błąd pobierania listy');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load('');
  }, []);

  async function onAdd() {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;

    setLoading(true);
    try {
      await upsertRecipient(email);
      setNewEmail('');
      await load(q);
      showOk('Email dodany / zaktualizowany');
    } catch (e) {
      showError(e, 'Błąd dodawania');
    } finally {
      setLoading(false);
    }
  }

  async function onToggleActive(recipient) {
    setLoading(true);
    try {
      await setRecipientActive(recipient.id, !recipient.isActive);
      await load(q);
      showOk('Zaktualizowano status');
    } catch (e) {
      showError(e, 'Błąd zmiany statusu');
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'user',
        headerName: 'Użytkownik',
        width: 180,
        valueGetter: (params) =>
          params.row.user
            ? `${params.row.user.firstName} ${params.row.user.lastName}`
            : '',
      },
      {
        field: 'isActive',
        headerName: 'Aktywny',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Switch
            checked={Boolean(params.row.isActive)}
            onChange={() => onToggleActive(params.row)}
            disabled={loading}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Akcje',
        width: 140,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            {user.users.role === 'ADMIN' ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={loading}
                onClick={async () => {
                  const ok = window.confirm(
                    `Usunąć odbiorcę ${params.row.email} ?\nZostanie usunięty też z wszystkich grup.`,
                  );
                  if (!ok) return;

                  setLoading(true);
                  try {
                    await deleteRecipientCascade(params.row.id);
                    await load(q);
                    showOk(
                      'Usunięto odbiorcę i jego członkostwa w grupach adresowych',
                    );
                  } catch (e) {
                    showError(e, 'Błąd usuwania');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Usuń
              </Button>
            ) : null}
          </Stack>
        ),
      },
    ],
    [loading], // eslint-disable-line react-hooks/exhaustive-deps
  );
  console.log('rows', rows);
  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">{t('masterData.mailingList')}</Typography>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* SEARCH */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Szukaj email (prefix)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load(q)}
              />
              <Button
                variant="contained"
                onClick={() => load(q)}
                disabled={loading}
              >
                Szukaj
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setQ('');
                  load('');
                }}
                disabled={loading}
              >
                Reset
              </Button>
            </Stack>

            <Divider />

            {/* ADD */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Dodaj email (asahibeer.pl / asahibeer.com / eu.asahibeer.pl / eu.asahibeer.com)"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAdd()}
              />
              <Button variant="contained" onClick={onAdd} disabled={loading}>
                Dodaj
              </Button>
            </Stack>

            {loading ? (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Ładowanie...
              </Alert>
            ) : null}
          </Stack>
        </Paper>

        {/* GRID */}
        <Paper sx={{ height: 520, width: '100%', p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
          />
        </Paper>
      </Stack>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecipientsPage;
